import React, { useState, useEffect } from 'react';
import {
  Paper,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  IconButton,
  Box,
  useTheme,
  useMediaQuery,
  Badge,
} from '@mui/material';
import {
  Layers as LayersIcon,
  LocationOn as LocationIcon,
  Warning as WarningIcon,
  LocalHospital as HospitalIcon,
  Home as HomeIcon,
  HelpOutline as HelpIcon,
} from '@mui/icons-material';
import { useMap } from '../../contexts/MapContext';

const ControlPanel: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const { selectedDataLayer, setSelectedDataLayer, resetGazaPosition, isInfoPanelExpanded } = useMap();
  const [showLayers, setShowLayers] = useState(false);
  const [showLayerTooltip, setShowLayerTooltip] = useState(false);
  const [showHelpTooltip, setShowHelpTooltip] = useState(false);

  // Show tooltips for first-time users
  useEffect(() => {
    const hasSeenLayerTooltip = localStorage.getItem('hasSeenLayerTooltip');
    if (!hasSeenLayerTooltip) {
      setTimeout(() => setShowLayerTooltip(true), 3000);
    }

    const hasSeenHelpTooltip = localStorage.getItem('hasSeenHelpTooltip');
    if (!hasSeenHelpTooltip) {
      setTimeout(() => setShowHelpTooltip(true), 5000);
    }
  }, []);

  const handleLayerChange = (
    _event: React.MouseEvent<HTMLElement>,
    newLayer: string | null,
  ) => {
    setSelectedDataLayer(newLayer);
  };

  const handleLayersClick = () => {
    setShowLayers(!showLayers);
    if (showLayerTooltip) {
      setShowLayerTooltip(false);
      localStorage.setItem('hasSeenLayerTooltip', 'true');
    }
  };

  const handleHelpClick = () => {
    if (showHelpTooltip) {
      setShowHelpTooltip(false);
      localStorage.setItem('hasSeenHelpTooltip', 'true');
    }
    // Reset all tooltips to show help again
    localStorage.removeItem('hasSeenOverlayTooltip');
    localStorage.removeItem('hasSeenLayerTooltip');
    localStorage.removeItem('hasSeenHelpTooltip');
    window.location.reload();
  };

  return (
    <Paper
      elevation={3}
      sx={{
        position: 'absolute',
        top: isMobile ? 'auto' : 80,
        bottom: isMobile ? (isInfoPanelExpanded ? '45vh' : 80) : 'auto',
        right: 16,
        zIndex: 1000,
        backgroundColor: 'rgba(255, 255, 255, 0.95)',
        backdropFilter: 'blur(10px)',
        borderRadius: 2,
        overflow: 'hidden',
        transition: 'bottom 0.3s ease',
      }}
    >
      <Box sx={{ p: 1 }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
          <Tooltip 
            title="Toggle Data Layers" 
            placement="left"
            open={showLayerTooltip}
            arrow
          >
            <Badge
              color="secondary"
              variant="dot"
              invisible={!showLayerTooltip}
            >
              <IconButton
                color={showLayers ? 'primary' : 'default'}
                onClick={handleLayersClick}
              >
                <LayersIcon />
              </IconButton>
            </Badge>
          </Tooltip>

          {showLayers && (
            <ToggleButtonGroup
              orientation="vertical"
              value={selectedDataLayer}
              exclusive
              onChange={handleLayerChange}
              size="small"
            >
              <ToggleButton value="damage" aria-label="damage">
                <Tooltip title="Damage Assessment" placement="left">
                  <WarningIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="health" aria-label="health">
                <Tooltip title="Health Facilities" placement="left">
                  <HospitalIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
              <ToggleButton value="displacement" aria-label="displacement">
                <Tooltip title="Displacement Orders" placement="left">
                  <LocationIcon fontSize="small" />
                </Tooltip>
              </ToggleButton>
            </ToggleButtonGroup>
          )}

          <Tooltip title="Reset Overlay Position" placement="left">
            <IconButton onClick={resetGazaPosition}>
              <HomeIcon />
            </IconButton>
          </Tooltip>

          <Tooltip 
            title="Show Help Tips" 
            placement="left"
            open={showHelpTooltip}
            arrow
          >
            <Badge
              color="secondary"
              variant="dot"
              invisible={!showHelpTooltip}
            >
              <IconButton onClick={handleHelpClick}>
                <HelpIcon />
              </IconButton>
            </Badge>
          </Tooltip>
        </Box>
      </Box>
      
      {showLayerTooltip && (
        <Box
          sx={{
            position: 'absolute',
            right: '100%',
            top: '10px',
            mr: 1,
            p: 1.5,
            backgroundColor: 'secondary.main',
            color: 'white',
            borderRadius: 1,
            fontSize: '12px',
            whiteSpace: 'nowrap',
            '&::after': {
              content: '""',
              position: 'absolute',
              left: '100%',
              top: '50%',
              transform: 'translateY(-50%)',
              borderLeft: '8px solid',
              borderLeftColor: 'secondary.main',
              borderTop: '6px solid transparent',
              borderBottom: '6px solid transparent',
            },
          }}
        >
          Click to view data layers
        </Box>
      )}
    </Paper>
  );
};

export default ControlPanel;